import React, { useState, useEffect, useCallback } from 'react';
import { Server, Database, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { useAdminSettings } from '../../../hooks/useAdminSettings';

const SystemSettingsTab = () => {
  const { apiCall, loading, setLoading, showSuccess, showError } = useAdminSettings();
  const [systemSettings, setSystemSettings] = useState({});

  const fetchSystemSettings = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/admin/settings/system');
      setSystemSettings(data);
    } catch (error) {
      console.error('Error fetching system settings:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchSystemSettings();
  }, [fetchSystemSettings]);

  const handleSystemToggle = async (key, value) => {
    try {
      await apiCall('put', '/api/admin/settings/system', { [key]: value });
      setSystemSettings(prev => ({ ...prev, [key]: value }));
      showSuccess('Setting updated');
    } catch (error) {
      showError('Failed to update setting');
    }
  };

  const handleBackup = async () => {
    setLoading(true);
    try {
      await apiCall('post', '/api/admin/settings/backup');
      showSuccess('Backup initiated');
    } catch (error) {
      showError('Failed to create backup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Feature Toggles */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Feature Toggles</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
            <div>
              <p className="text-white font-medium">Enable Uploads</p>
              <p className="text-sm text-gray-400">Allow users to upload content</p>
            </div>
            <Switch
              checked={systemSettings.uploads_enabled ?? true}
              onCheckedChange={(checked) => handleSystemToggle('uploads_enabled', checked)}
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
            <div>
              <p className="text-white font-medium">Enable Messaging</p>
              <p className="text-sm text-gray-400">Allow users to send messages</p>
            </div>
            <Switch
              checked={systemSettings.messaging_enabled ?? true}
              onCheckedChange={(checked) => handleSystemToggle('messaging_enabled', checked)}
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
            <div>
              <p className="text-white font-medium">Enable Referrals</p>
              <p className="text-sm text-gray-400">Allow referral program</p>
            </div>
            <Switch
              checked={systemSettings.referrals_enabled ?? true}
              onCheckedChange={(checked) => handleSystemToggle('referrals_enabled', checked)}
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
            <div>
              <p className="text-white font-medium">Maintenance Mode</p>
              <p className="text-sm text-gray-400">Put platform in maintenance mode</p>
            </div>
            <Switch
              checked={systemSettings.maintenance_mode ?? false}
              onCheckedChange={(checked) => handleSystemToggle('maintenance_mode', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Integration Status */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Integration Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">AWS S3</span>
                {systemSettings.integrations?.aws_s3?.configured ? (
                  <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="w-3 h-3 mr-1" /> Connected</Badge>
                ) : (
                  <Badge className="bg-red-500/20 text-red-400"><XCircle className="w-3 h-3 mr-1" /> Not Configured</Badge>
                )}
              </div>
              <p className="text-xs text-gray-400">Bucket: {systemSettings.integrations?.aws_s3?.bucket || 'N/A'}</p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">Stripe</span>
                {systemSettings.integrations?.stripe?.configured ? (
                  <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="w-3 h-3 mr-1" /> {systemSettings.integrations?.stripe?.mode}</Badge>
                ) : (
                  <Badge className="bg-red-500/20 text-red-400"><XCircle className="w-3 h-3 mr-1" /> Not Configured</Badge>
                )}
              </div>
              <p className="text-xs text-gray-400">Payment processing</p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">SendGrid</span>
                {systemSettings.integrations?.sendgrid?.configured ? (
                  <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="w-3 h-3 mr-1" /> Connected</Badge>
                ) : (
                  <Badge className="bg-red-500/20 text-red-400"><XCircle className="w-3 h-3 mr-1" /> Not Configured</Badge>
                )}
              </div>
              <p className="text-xs text-gray-400">Email delivery</p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">Twilio</span>
                {systemSettings.integrations?.twilio?.configured ? (
                  <Badge className="bg-yellow-500/20 text-yellow-400"><AlertTriangle className="w-3 h-3 mr-1" /> Dev Mode</Badge>
                ) : (
                  <Badge className="bg-red-500/20 text-red-400"><XCircle className="w-3 h-3 mr-1" /> Not Configured</Badge>
                )}
              </div>
              <p className="text-xs text-gray-400">SMS verification</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Database Backup */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Database className="w-5 h-5" />
            Database Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
            <div>
              <p className="text-white font-medium">Create Backup</p>
              <p className="text-sm text-gray-400">Create a snapshot of the database</p>
            </div>
            <Button onClick={handleBackup} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              <Database className="w-4 h-4 mr-2" />
              Backup Now
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemSettingsTab;
